import { useState, useEffect, useRef } from 'react'
import { ChevronDown, ChevronRight, Brain } from 'lucide-react'

interface ThinkingAccordionProps {
  thinking: string
  /** True while the model is still generating thinking content. */
  isActive?: boolean
}

export const ThinkingAccordion: React.FC<ThinkingAccordionProps> = ({
  thinking,
  isActive = false,
}) => {
  // Auto-open while streaming; collapse when done.
  const [isOpen, setIsOpen] = useState(isActive)
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isActive) setIsOpen(true)
  }, [isActive])

  // Auto-scroll to bottom while streaming.
  useEffect(() => {
    if (isOpen && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight
    }
  }, [thinking, isOpen])

  if (!thinking && !isActive) return null

  return (
    <div
      className="mb-2 rounded-xl overflow-hidden"
      style={{ border: '1px solid #e9d8f5', background: '#faf5ff' }}
    >
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors"
        style={{ background: isOpen ? '#f3e8ff' : '#faf5ff' }}
      >
        {/* Brain icon — pulses while active */}
        <Brain
          className="w-3.5 h-3.5 shrink-0"
          style={{
            color: '#9333ea',
            animation: isActive ? 'think-pulse 1.4s ease-in-out infinite' : 'none',
          }}
        />

        <span
          className="flex-1 text-xs font-medium"
          style={{ color: '#7e22ce' }}
        >
          {isActive ? 'Thinking…' : 'Thought process'}
        </span>

        {/* Streaming dots or chevron */}
        {isActive ? (
          <span className="flex gap-0.5 items-center">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-1 h-1 rounded-full"
                style={{
                  background: '#9333ea',
                  opacity: 0.6,
                  animation: `think-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </span>
        ) : isOpen ? (
          <ChevronDown className="w-3.5 h-3.5" style={{ color: '#a855f7' }} />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" style={{ color: '#a855f7' }} />
        )}
      </button>

      {isOpen && (
        <div
          ref={bodyRef}
          className="px-3 py-2.5 max-h-48 overflow-y-auto"
          style={{ borderTop: '1px solid #e9d8f5', background: '#ffffff' }}
        >
          <p
            className="text-xs leading-relaxed whitespace-pre-wrap font-mono"
            style={{ color: '#6b21a8', wordBreak: 'break-word' }}
          >
            {thinking}
            {isActive && (
              <span
                className="inline-block w-0.5 h-3 ml-0.5 align-middle"
                style={{ background: '#9333ea', animation: 'think-caret 1s step-end infinite' }}
              />
            )}
          </p>
        </div>
      )}

      <style>{`
        @keyframes think-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
        @keyframes think-dot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
        @keyframes think-caret {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </div>
  )
}
